import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, XCircle } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import {
  EVACUATION_EVENT_STATUSES,
  TABLE_IDS,
} from "../../../constants/common";
import { cn } from "../../../utils/cn";
import { EvacuationEvent } from "../../../hooks/emergency-evacuation/interface";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";
import CloseEvacuationEventModal from "../../modal/CloseEvacuationEventModal";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";

interface EvacuationEventsTableProps {
  allEvacuationEvents: EvacuationEvent[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const EvacuationEventsTable = ({
  allEvacuationEvents,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: EvacuationEventsTableProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [isCloseEvacuationEventModalOpen, setIsCloseEvacuationEventModalOpen] =
    useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const handleCloseEvent = useCallback(async (eventId: number) => {
    setSelectedEventId(eventId);
    setIsCloseEvacuationEventModalOpen(true);
  }, []);

  const columns: ColumnDef<EvacuationEvent>[] = useMemo(
    () => [
      {
        accessorKey: "n_EvacuationID",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("eventId")}
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                navigate(
                  `${APP_ROUTES.UPDATE_EVACUATION}?ID=${row?.original?.n_EvacuationID.toString()}`,
                )
              }
              className="text-blue-800 underline cursor-pointer"
            >
              {row.original.n_EvacuationID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </p>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </p>
          );
        },
      },
      {
        accessorKey: "s_FloorNo",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("floor")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_MusterPointNames_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPoints")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const musterPoints =
            i18n.language === "en"
              ? row.original.s_MusterPointNames_En?.split(", ")
              : row.original.s_MusterPointNames_Ar?.split(", ");

          return (
            <div className="flex flex-col space-y-1">
              {musterPoints?.map((musterPoint, index) => (
                <div key={index} className="whitespace-nowrap">
                  {musterPoint}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "eventType",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("eventType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>
              {i18n.language === "en"
                ? row.original.s_EventType_En
                : row.original.s_EventType_Ar}
            </p>
          );
        },
      },
      // {
      //   accessorKey: "s_EventPersonType_En",
      //   header: ({ column }) => (
      //     <Button
      //       className="flex w-full justify-between text-md"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("participantType")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      //   cell: ({ row }) => {
      //     const participantTypes =
      //       i18n.language === "en"
      //         ? row.original.s_EventPersonType_En?.split(", ")
      //         : row.original.s_EventPersonType_Ar?.split(", ");

      //     return (
      //       <div className="flex flex-col space-y-1">
      //         {participantTypes?.map((participantType, index) => (
      //           <div key={index} className="whitespace-nowrap">
      //             {participantType}
      //           </div>
      //         ))}
      //       </div>
      //     );
      //   },
      // },
      {
        accessorKey: "dt_EventStartTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("startTime")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>{formattedDateTimeWithoutSec(row.original.dt_EventStartTime)}</p>
          );
        },
      },
      {
        accessorKey: "dt_EventEndTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("endTime")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.dt_EventEndTime) return;

          return (
            <p>{formattedDateTimeWithoutSec(row.original.dt_EventEndTime)}</p>
          );
        },
      },
      {
        accessorKey: "s_CreatedBy",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("createdBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Status_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.s_StatusCode === EVACUATION_EVENT_STATUSES.ACTIVE
                  ? "bg-green-600"
                  : "bg-gray-500",
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_Status_En
                  : row.original.s_Status_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_Reason",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("reason")}
          </Button>
        ),
      },
      {
        accessorKey: "action",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("action")}
          </Button>
        ),
        cell: ({ row }) => {
          return (
            row.original.s_StatusCode === EVACUATION_EVENT_STATUSES.ACTIVE && (
              <Button
                Icon={<XCircle className="w-5" />}
                onClick={() => handleCloseEvent(row.original.n_EvacuationID)}
                className="py-1 "
              >
                {t("close")}
              </Button>
            )
          );
        },
      },
    ],
    [handleCloseEvent, i18n.language, navigate, t],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allEvacuationEvents}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.EVACUATION_EVENTS}
      />

      <CloseEvacuationEventModal
        isOpen={isCloseEvacuationEventModalOpen}
        onOpenChange={() => setIsCloseEvacuationEventModalOpen(false)}
        eventId={selectedEventId}
      />
    </>
  );
};

export default memo(EvacuationEventsTable);
